"use client"

import { type FormEvent, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Card, Form, Button, Spinner, Toast } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { LanguageSwitcher } from "../../components/language-switcher"

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState("")
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success")
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Import Firebase auth functions
      const { sendPasswordResetEmail } = await import("firebase/auth")
      const { auth } = await import("../../firebase/config")

      // Send password reset email directly using Firebase client SDK
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/auth/login`,
        handleCodeInApp: false,
      })

      setToastVariant("success")
      setToastMessage(
        t("passwordResetEmailSent") ||
          "Password reset email sent successfully. Please check your email for instructions.",
      )
      setShowToast(true)

      // Navigate to login after showing success message
      setTimeout(() => {
        navigate("/auth/login")
      }, 3000)
    } catch (error: any) {
      console.error("Forgot password error:", error)

      // Handle Firebase Auth errors
      let errorMessage = t("failedToSendResetEmail") || "Failed to send password reset email"

      switch (error.code) {
        case "auth/user-not-found":
          errorMessage = t("noAccountFound") || "No account found with this email address"
          break
        case "auth/invalid-email":
          errorMessage = t("invalidEmail") || "Please enter a valid email address"
          break
        case "auth/too-many-requests":
          errorMessage = t("tooManyRequests") || "Too many password reset requests. Please try again later"
          break
        case "auth/network-request-failed":
          errorMessage = t("networkError") || "Network error. Please check your connection and try again"
          break
        default:
          errorMessage = error.message || t("failedToSendResetEmail") || "Failed to send password reset email"
      }

      setToastVariant("danger")
      setToastMessage(errorMessage)
      setShowToast(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center bg-light p-3">
      <div className="position-absolute top-0 end-0 p-3">
        <LanguageSwitcher />
      </div>
      <div className="mb-4 d-flex align-items-center gap-2">
        <img
          src="/logo.svg?height=40&width=40"
          alt="Unity Smart Living Logo"
          width={40}
          height={40}
          className="rounded"
        />
        <span className="fs-3 fw-bold">Unity Smart Living</span>
      </div>
      <Card className="w-100" style={{ maxWidth: "400px" }}>
        <Card.Header className="text-center">
          <Card.Title>{t("forgotPassword")}</Card.Title>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t("emailAddress")}</Form.Label>
              <Form.Control
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>
            <Button type="submit" variant="primary" className="w-100" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {t("loading")}
                </>
              ) : (
                t("resetPassword")
              )}
            </Button>
          </Form>
          <div className="mt-3 text-center small">
            <Link to="/auth/login" className="text-primary text-decoration-none">
              {t("backToLogin")}
            </Link>
          </div>
        </Card.Body>
      </Card>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={5000}
        autohide
        className="position-fixed bottom-0 end-0 m-3"
        bg={toastVariant}
      >
        <Toast.Header>
          <strong className="me-auto">{toastVariant === "success" ? t("success") : t("error")}</strong>
        </Toast.Header>
        <Toast.Body className={toastVariant === "danger" ? "text-white" : undefined}>{toastMessage}</Toast.Body>
      </Toast>
    </div>
  )
}
